import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PermissionsService } from "../../services/permissions/permissions.service";
import moment from "moment-timezone";
import { ReportService } from 'src/app/services/report/report.service';

@Component({
  selector: 'app-generate-report',
  templateUrl: './generate-report.component.pug',
  styleUrls: ['./generate-report.component.scss'],
  standalone: false
})
export class GenerateReportComponent implements OnInit {

  @Input() filter = 'All';
  @Input() start!: any;
  @Input() end!: any;
  @Output() closeEvent = new EventEmitter<string>();

  ENT_10363_PULSE_RELAUNCH = false;

  dateDropdownOptions = [
    {key: 'custom', type: 'option', value: $localize`Custom`},
    {key: 'all', type: 'option', value: $localize`All`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Days`},
    {key: 'last_7_days', type: 'option', value: $localize`Last 7 days`},
    {key: 'last_30_days', type: 'option', value: $localize`Last 30 days`},
    {key: 'last_60_days', type: 'option', value: $localize`Last 60 days`},
    {key: 'last_90_days', type: 'option', value: $localize`Last 90 days`},
    {key: 'last_365_days', type: 'option', value: $localize`Last 365 days`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar weeks`},
    {key: 'current_week', type: 'option', value: $localize`Current week`},
    {key: 'last_week', type: 'option', value: $localize`Last week`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar months`},
    {key: 'current_month', type: 'option', value: $localize`Current month`},
    {key: 'last_month', type: 'option', value: $localize`Last month`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar quarters`},
    {key: 'current_quarter', type: 'option', value: $localize`Current quarter`},
    {key: 'last_quarter', type: 'option', value: $localize`Last quarter`},
    {type: 'separator'},
    {type: 'heading', label: $localize`Calendar years`},
    {key: 'current_year', type: 'option', value: $localize`Current year`},
    {key: 'last_year', type: 'option', value: $localize`Last year`},
  ];
  loading = false;
  selectedDate = $localize`Last 30 days`;
  showDatesRow = false;
  selectedItems = [];
  dropdownPlaceholder = $localize`Select the report type`;
  fromDateLabel = $localize`Start Date`;
  toDateLabel = $localize`End Date`;
  showStartCalendar = false;
  showEndCalendar = false;
  startStr = '';
  endStr = '';
  disableGenerateBtn = true;
  items: any[] = [];

  constructor(
    private modalService: BsModalService,
    private reportService: ReportService,
    private permissionsService: PermissionsService,
  ) { }

  ngOnInit(): void {
    this.ENT_10363_PULSE_RELAUNCH = this.permissionsService.isFeatureActivated('ent-10363-pulse-relaunch');
    this.initializeItemsBasedOnPermissions();
    this.calculateLastDays(30);
  }

  initializeItemsBasedOnPermissions(): void {
    const allItems = [
      // Keys should be equal to ChoiceField in Report model.
      {
        "name": $localize`Pulse report`,
        "key": "pulse_export"
      },
      {
        "name": $localize`Talent pool`,
        "key": "talent_pool"
      }
    ];

    this.items = allItems.filter(item => {
      switch (item.key) {
        case 'pulse_export':
          return this.permissionsService.hasPermission('can_generate_and_view_pulse_report') ||
                 this.permissionsService.hasPermission('can_generate_and_view_pulse_report_with_survey_perms');
        case 'talent_pool':
          return this.permissionsService.hasPermission('can_view_or_generate_talent_pool_export_reports');
        default:
          return false;
      }
    });
  }

  closeModal(reason): void {
    this.modalService.hide();
    this.closeEvent.emit(reason);
  }

  reset(){
    this.selectedItems = [];
    this.items.forEach((item: any) => {item.checked = false;})
    this.disableGenerateBtn = true;
  }
  selectedItemsEmitter(items: any){
    this.selectedItems = items;
    this.disableGenerateBtn = !items.some((item: any) => item.checked);
  }

  calculateLastDays(days: number) {
    this.showDatesRow = false;
    this.start = moment().startOf('day').subtract(days, 'days');
    this.end = moment();
  }


  calculateCalendarDates(filter: string) {
    this.showDatesRow = false;
    switch (filter) {
      case 'currentWeek':
        this.start = moment().startOf('week');
        this.end = moment().endOf('week');
        break;
      case 'lastWeek':
        this.start = moment().subtract(1, 'weeks').startOf('week');
        this.end = moment().subtract(1, 'weeks').endOf('week');
        break;
      case 'currentMonth':
        this.start = moment().startOf('month');
        this.end = moment().endOf('month');
        break;
      case 'lastMonth':
        this.start = moment().subtract(1, 'months').startOf('month');
        this.end = moment().subtract(1, 'months').endOf('month');
        break;
      case 'currentQuarter':
        this.start = moment().startOf('quarter');
        this.end = moment().endOf('quarter');
        break;
      case 'lastQuarter':
        this.start = moment().subtract(1, 'quarters').startOf('quarter');
        this.end = moment().subtract(1, 'quarters').endOf('quarter');
        break;
      case 'currentYear':
        this.start = moment().startOf('year');
        this.end = moment().endOf('year');
        break;
      case 'lastYear':
        this.start = moment().subtract(1, 'years').startOf('year');
        this.end = moment().subtract(1, 'years').endOf('year');
        break;
    }
  }


  optInDateDropdownChange(event: any) {
    const value = event.value;
    this.selectedDate = value;
    this.filter = value;

    if (value === $localize`Custom`) {
      this.showDatesRow = true;
      this.start = moment().startOf('day').subtract(30, 'days');
      this.end = moment();
    } else {
      this.showDatesRow = false;
    }

    if (event?.key === 'all') {
      this.start = moment('1970-01-01T00:00:00.000');
      this.end = moment();
    }

    // Match days filters
    const daysMap = {
      [$localize`Last 7 days`]: 7,
      [$localize`Last 30 days`]: 30,
      [$localize`Last 60 days`]: 60,
      [$localize`Last 90 days`]: 90,
      [$localize`Last 365 days`]: 365
    };

    if (value in daysMap) {
      this.calculateLastDays(daysMap[value]);
    }

    // Match calendar-based filters
    const calendarFilters = [
      { label: $localize`Current week`, key: 'currentWeek' },
      { label: $localize`Last week`, key: 'lastWeek' },
      { label: $localize`Current month`, key: 'currentMonth' },
      { label: $localize`Last month`, key: 'lastMonth' },
      { label: $localize`Current quarter`, key: 'currentQuarter' },
      { label: $localize`Last quarter`, key: 'lastQuarter' },
      { label: $localize`Current year`, key: 'currentYear' },
      { label: $localize`Last year`, key: 'lastYear' },
    ];

    const selectedFilter = calendarFilters.find(f => f.label === value);
    if (selectedFilter) {
      this.calculateCalendarDates(selectedFilter.key);
    }
  }

  onCalendarVisibilityChange(event: { showStart: boolean; showEnd: boolean }) {
    this.showStartCalendar = event.showStart;
    this.showEndCalendar = event.showEnd;
  }

  onDateChange(dates: { start: moment.Moment, end: moment.Moment }) {
    this.start = dates.start;
    this.end = dates.end;
  }

  generateReport(): void{
    /**
     * Builds report_types array from user-selected items in the multi-select dropdown.
     * Supports multiple report types: 'pulse', 'exit', and 'talent_pool'.
     * The selectedItems array is populated via selectedItemsEmitter() when user makes selections.
     */
    const reportTypes = this.selectedItems
      .filter((item: any) => item.checked)
      .map((selectedItem: any) => {
        // Find the corresponding item in the items array to get the key (pulse or talent_pool)
        const item = this.items.find(i => i.name === selectedItem.name);
        return item ? item.key : null;
      })
      .filter((key: string | null) => key !== null) as string[];

    if (reportTypes.length === 0) {
      console.error('No report types selected');
      return;
    }

    const data = {
      'report_types': reportTypes, // Dynamically built from user selections, not hardcoded
      'from_date': moment(this.start).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      'to_date': moment(this.end).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      'timezone': moment.tz.guess(),
    };
    this.loading = true;
    this.reportService.generateAndExportReport(data).subscribe({
      next: (data) => {
         this.loading = false;
         this.closeModal('generate-report-clicked');
      },
      error: (error) => {
        this.loading = false;
        console.error('Failed to generate report:', error);
        // Could emit error event or show error message
      }
    });
  }
}

